import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, createVNode as _createVNode, normalizeStyle as _normalizeStyle, Teleport as _Teleport, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["id"]

import Vue, { onMounted, ref, onBeforeUnmount, watch, nextTick } from 'vue';
import Suggest from '@/corePagesModule/components/Suggest.vue';
import SvgLoader from '@/utils/SvgLoader.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'SelectableField',
  props: {
    suggestions: {},
    selectedId: {},
    value: {},
    defaultValue: {},
    hint: {},
    prefix: {},
    suffix: {}
  },
  emits: ["update-value", "update-selected-id"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const result = ref<DOMRect>();
const xLength = ref(0);
const yLength = ref(0);
const isOpen = ref(false);

// 自分のルート要素と、Teleport先のラッパー要素
const rootEl = ref<HTMLElement | null>(null);
const portalEl = ref<HTMLElement | null>(null);

// 直前の“開いたクリック”を無視するためのフラグ
let ignoreNextDocumentPointerDown = false;

/**
 * ドキュメント外クリックハンドラ
 * Teleport先(portalEl)と自分(rootEl)を両方内側扱いにする
 * @param e - PointerEvent
 */
const onDocumentPointerDown = (e: PointerEvent) => {
  if (!isOpen.value) return;
  if (ignoreNextDocumentPointerDown) return;

  const path = (e.composedPath && e.composedPath()) || [];
  const clickedInsideRoot = rootEl.value ? path.includes(rootEl.value) : false;
  const clickedInsidePortal = portalEl.value ? path.includes(portalEl.value) : false;

  if (clickedInsideRoot || clickedInsidePortal) return;

  // どちらでもなければ外側クリック → 閉じる
  isOpen.value = false;
};

/**
 * ドキュメントのポインターイベントを登録
 */
const addDocListener = () => {
  // “開いたクリック”と同一イベントで即発火しないよう、次フレームで登録
  ignoreNextDocumentPointerDown = true;
  setTimeout(() => {
    document.addEventListener('pointerdown', onDocumentPointerDown, true);
    // 1フレームだけ無視すれば十分
    ignoreNextDocumentPointerDown = false;
  }, 0);
};

/**
 * ドキュメントのポインターイベントを削除
 */
const removeDocListener = () => {
  document.removeEventListener('pointerdown', onDocumentPointerDown, true);
};

const emits = __emit;

/**
 * クリックされたらinputを表示してフォーカスする
 */
const onClickLabel = async () => {
  await nextTick();
  document.getElementById('selectable-input-container__input')?.focus();

  const element = document.getElementById(selectableInputContainerId.value);
  if (!element) return;

  // 位置計算
  result.value = element.getBoundingClientRect();
  xLength.value = result.value.left;
  yLength.value = result.value.bottom;

  // 開閉トグル
  const nextState = !isOpen.value;
  isOpen.value = !isOpen.value;

  // 選択されているIDを更新
  if (nextState) {
    emits('update-selected-id', selectableInputContainerId.value);
    // Teleport描画が反映されたあとに portalEl を掴んで外クリック監視を開始
    await nextTick();
    addDocListener();
  } else {
    removeDocListener();
  }
};

/**
 * サジェストが選択された時の処理
 * @param suggest - 選択されたサジェスト
 */
const onClickSuggest = (suggest: string) => {
  isOpen.value = false;
  removeDocListener();
  emits('update-value', suggest);
};

// ランダムID生成
const selectableInputContainerId = ref<string>('');

onMounted(() => {
  selectableInputContainerId.value = Math.random().toString(32).substring(2);
});

onBeforeUnmount(() => {
  removeDocListener();
});

watch(
  () => props.selectedId,
  (newValue) => {
    if (newValue !== selectableInputContainerId.value) {
      isOpen.value = false;
      removeDocListener();
    }
  }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    id: selectableInputContainerId.value,
    ref_key: "rootEl",
    ref: rootEl,
    class: "selectable-input-container"
  }, [
    _createElementVNode("div", {
      class: "label-container rounded-base-small",
      onClick: onClickLabel
    }, [
      _createElementVNode("div", {
        class: _normalizeClass(["label-container__text selectable-input-container__text-box selectable-input-container__text-box--label", { 'label-container--content': _ctx.value, 'label-container--hint': !_ctx.value }])
      }, _toDisplayString(_ctx.value ?? _ctx.hint), 3),
      _createVNode(SvgLoader, {
        class: "label-container__icon",
        name: 'arrowDown'
      })
    ]),
    (_ctx.suggestions.length > 0 && isOpen.value)
      ? (_openBlock(), _createBlock(_Teleport, {
          key: 0,
          to: "body"
        }, [
          _createElementVNode("div", {
            ref_key: "portalEl",
            ref: portalEl
          }, [
            _createVNode(Suggest, {
              class: "selectable-input-container__suggests rounded-base-small",
              style: _normalizeStyle({
            top: `${yLength.value}px`,
            left: `${xLength.value}px`,
          }),
              options: _ctx.suggestions,
              "min-width": `${result.value?.width}px`,
              onClickSuggest: onClickSuggest
            }, null, 8, ["style", "options", "min-width"])
          ], 512)
        ]))
      : _createCommentVNode("", true)
  ], 8, _hoisted_1))
}
}

})